<template>
  <header class="global-header__sp">
    <div class="global-header__sp-top">
      <SpaInternalLink href="/" class="global-header__sp-brand">
        <img :src="logoImage" alt="サービスロゴイメージ" class="sp-brand__icon" />
        <img :src="logoLabel" alt="サービスロゴラベル" class="sp-brand__label" />
      </SpaInternalLink>
      <div class="global-header__sp-actions">
        <template v-if="props.isLoading">
          <!-- loading中は非表示（ロゴのみ） -->
        </template>
        <template v-else-if="!isLoggedIn">
          <GuestHeaderAuthLinks variant="responsive" />
        </template>
        <template v-else>
          <button
            type="button"
            class="sp-action sp-action--icon"
            aria-label="検索"
            @click="goSearch"
          >
            <img :src="searchIcon" alt="" aria-hidden="true" />
          </button>
          <button
            type="button"
            class="sp-action sp-action--icon"
            aria-label="メニューを開く"
            @click="toggleSpMenu"
          >
            <img :src="menuIcon" alt="" aria-hidden="true" />
          </button>
        </template>
      </div>
    </div>

    <div
      v-if="isLoggedIn && !props.isLoading"
      class="sp-drawer"
      :class="{ 'is-open': isSpMenuOpen }"
      aria-label="メニュー"
      @click.self="closeSpMenu"
    >
      <div class="sp-drawer__panel">
        <div class="sp-drawer__header">
          <SpaInternalLink href="/" class="sp-drawer__brand" @click="closeSpMenu">
            <img :src="logoImageWhite" alt="サービスロゴイメージ" class="sp-brand__icon" />
            <img :src="logoLabelWhite" alt="サービスロゴラベル" class="sp-brand__label" />
          </SpaInternalLink>
        </div>
        <div class="sp-drawer__links">
          <SpaInternalLink href="/" class="sp-drawer__link" @click="closeSpMenu">
            マイページ
          </SpaInternalLink>
        </div>
        <div class="sp-drawer__settings">
          <div class="sp-drawer__label">設定</div>
          <template v-for="item in settingsMenuItems" :key="item.label">
            <SpaInternalLink
              v-if="item.href && item.action !== 'logout'"
              :href="item.href"
              class="sp-drawer__link"
              @click="closeSpMenu"
            >
              　{{ item.label }}
            </SpaInternalLink>
            <button
              v-else-if="item.action === 'logout'"
              class="sp-drawer__link sp-drawer__link--button"
              type="button"
              @click="
                () => {
                  onLogout();
                  closeSpMenu();
                }
              "
            >
              {{ item.label }}
            </button>
            <button v-else class="sp-drawer__link sp-drawer__link--button" type="button">
              {{ item.label }}
            </button>
          </template>
        </div>
      </div>
    </div>

    <SpBottomNav
      v-if="isLoggedIn && !props.isLoading"
      :items="bottomNavItems"
      :is-active="isActive"
    />
  </header>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import menuIcon from '@/assets/images/icons/global-header-menu.svg';
import searchIcon from '@/assets/images/icons/global-header-search.svg';
import logoImageWhite from '@/assets/images/icons/service-site-logo-white-image.svg';
import logoLabelWhite from '@/assets/images/icons/service-site-logo-white-label.svg';
import logoImage from '@/assets/images/service-site-logo-image.svg';
import logoLabel from '@/assets/images/service-site-logo-label.svg';
import SpaInternalLink from '@/components/shared/SpaInternalLink.vue';
import GuestHeaderAuthLinks from './GuestHeaderAuthLinks.vue';
import SpBottomNav from './SpBottomNav.vue';
import { settingsMenuItems, spBottomNavItems } from './navData';
import type { NavItem, SpBottomNavItem } from './types.d';

const props = defineProps<{
  isLoggedIn?: boolean;
  userName?: string;
  isActive: (item: NavItem) => boolean;
  goSearch: () => void;
  onLogout: () => void;
  isLoading?: boolean;
  considerationBadgeCount?: number;
  scoutBadgeCount?: number;
  selectionBadgeCount?: number;
  platform?: string;
}>();

const isSpMenuOpen = ref(false);
const bottomNavItems = computed<(SpBottomNavItem & { badgeCount?: number })[]>(() =>
  spBottomNavItems.map((item) => {
    const href =
      (props.platform === 'direct' && item.directHref) ||
      (props.platform !== 'direct' && item.agentHref) ||
      item.href;
    if (item.label === '気になる/検討中') {
      return { ...item, href, badgeCount: props.considerationBadgeCount };
    }
    if (item.label === 'スカウト/おすすめ') {
      return { ...item, href, badgeCount: props.scoutBadgeCount };
    }
    if (item.label === '選考管理') {
      return { ...item, href, badgeCount: props.selectionBadgeCount };
    }
    return { ...item, href };
  })
);

const displayName = computed(() => props.userName?.trim() ?? '');

const toggleSpMenu = () => {
  isSpMenuOpen.value = !isSpMenuOpen.value;
};

const closeSpMenu = () => {
  isSpMenuOpen.value = false;
};
</script>

<style scoped lang="scss">
@import '@/assets/css/_variables.scss';
@import '@/assets/stylesheets/components/_variables.scss';

.global-header__sp {
  display: flex;
  flex-direction: column;
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: $z-navigation;
  background: #ffffff;
  box-shadow: 0 4px 24px #1e1e1e17;
}

.global-header__sp-top {
  width: 100%;
  height: 72px;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  box-sizing: border-box;
  gap: 20px;
}

.global-header__sp-brand {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sp-brand__icon {
  width: 36px;
  height: 36px;
  object-fit: cover;
}

.sp-brand__label {
  width: 160px;
  height: auto;
  max-height: 100%;
}

.global-header__sp-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.sp-action {
  border: none;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.sp-action--icon img {
  width: 25px;
  height: 25px;
}

.sp-drawer {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.34);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  z-index: 1200;
  overflow: hidden;
}

.sp-drawer__panel {
  position: absolute;
  top: 0;
  left: 0;
  width: 315px;
  height: 100%;
  background: $primary-dark;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 8px;
  transform: translateX(-100%);
  transition: transform 0.24s ease;
}

.sp-drawer.is-open {
  opacity: 1;
  pointer-events: auto;
}

.sp-drawer.is-open .sp-drawer__panel {
  transform: translateX(0);
}

.sp-drawer__header {
  height: 72px;
  padding: 16px 8px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
}

.sp-drawer__brand {
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
}

.sp-drawer__links,
.sp-drawer__settings {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.sp-drawer__label {
  color: #ffffff;
  padding: 16px;
  font-size: 14px;
  font-weight: 500;
  border-top: 1px solid #ffffff;
  box-sizing: border-box;
}

.sp-drawer__link {
  color: #ffffff;
  text-decoration: none;
  padding: 12px 16px;
  border-top: 1px solid #ffffff;
  font-size: 14px;
  line-height: 1.6;
  box-sizing: border-box;
}

.sp-drawer__link:first-child {
  border-top: none;
}

.sp-drawer__link--button {
  text-align: left;
  width: 100%;
  background: transparent;
  border: none;
  cursor: pointer;
}

@media (max-width: 360px) {
  .global-header__sp-top {
    padding: 12px 10px;
    gap: 10px;
  }

  .global-header__sp-actions {
    gap: 10px;
  }

  .sp-brand__icon {
    width: 32px;
    height: 32px;
  }

  .sp-brand__label {
    width: 142px;
  }
}
</style>
